import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TrialPreEndModalNoTag,
  TrialPreEndModalStyled,
  TrialPreEndModalSubTitle,
  TrialPreEndModalTitle,
  TrialPreEndModalYourPerformance,
} from './TrialPreEndModal.styled'
import {
  Modal,
  Button,
  ModalControlsStyled,
  useFrontEndEventTrackingService,
  ComponentLoader,
} from '@clickcease/cc-client-components-lib'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { PaidMarketingData } from './PaidMarketingData/PaidMarketingData'
import { BotMitigationData } from './BotMitigation/BotMitigationData'
import { PRODUCT_TYPES, ProductTypes } from 'Types/ProductTypes'
import { BundleData } from './Bundle/Bundle'
import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { differenceInDays } from 'date-fns'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { useExtendTrial } from 'Features/accountSettings/api/extendTrial'
import { queryClient } from 'Lib/react-query'
import { usePricingEndOfTrial } from 'Hooks/split/usePricingEndOfTrial'

interface TrialEndedModalProps {
  isOpen?: boolean
  onOpenChange: (a: boolean) => void
}

export const TrialPreEndModal = ({ isOpen, onOpenChange }: TrialEndedModalProps) => {
  const { t } = useTranslation('accountSettings')
  const navigate = useNavigate()
  const { setModalToShow, showToaster } = useAppStore()
  const { track } = useFrontEndEventTrackingService()
  const { data: plansDetails, isLoading: isLoadingPlans } = useGetPlansDetails()
  const { data: domains, isLoading: isLoadingDomains } = useGetDomains()
  const extendTrail = useExtendTrial()
  const pricingEndOfTrial = usePricingEndOfTrial()
  const { data: currentPlanDetails } = useGetPlansDetails()
  const productToShow = useMemo(() => {
    const domainsHasTag = domains?.data?.domains.filter((domain) => domain.tagInstalled) || []
    if (domainsHasTag.length === 0) return 'noTag'
    else if (plansDetails?.plans.length === 1) {
      return plansDetails?.plans[0].product.toLocaleLowerCase() as ProductTypes
    } else {
      return PRODUCT_TYPES.BUNDLE
    }
  }, [domains, plansDetails])

  const trialPeriod = useMemo(() => {
    const diffInDays = differenceInDays(new Date(plansDetails?.noCreditCard || ''), new Date())
    return diffInDays >= 0 && diffInDays <= 2
  }, [plansDetails])

  const closeModal = () => {
    track(['segment'], trialPeriod ? 'Pre trial end popup closed' : 'Trial ended popup closed', {})
    onOpenChange(false)
  }

  const ableToExtendTrial = useMemo(() => {
    return productToShow === 'noTag' && trialPeriod && !plansDetails?.trial?.extendRequest
  }, [productToShow, trialPeriod, plansDetails])

  const handleClick = () => {
    if (ableToExtendTrial) {
      extendTrail.mutate(undefined, {
        onSuccess: () => {
          showToaster(t('trial_pre_end_modal.trial_extended_successfully'), 'success')
          track(['segment'], 'Pre trial end popup extend trial', {})
          setTimeout(() => {
            void queryClient.invalidateQueries(['getPlansDetails'])
          }, 1500)
          closeModal()
          navigate('/onboarding')
        },
      })
    } else {
      if (pricingEndOfTrial) goToPricing()
      else goToBilling()
    }
  }

  const goToBilling = () => {
    track(
      ['segment'],
      trialPeriod ? 'Pre trial end popup cta clicked' : 'Trial ended popup cta clicked',
      {}
    )
    navigate('/account-settings', {
      state: {
        openPaymentMethod: true,
        origin: trialPeriod ? 'trialPreEndModal' : 'TrialEndedModal',
      },
    })
    setModalToShow('')
  }

  const getPricingTab = () => {
    if (currentPlanDetails?.plans.length === 2) {
      return 2
    } else {
      return currentPlanDetails?.plans[0].product.toLowerCase() ===
        PRODUCT_TYPES.PAID_MARKETING.toLowerCase()
        ? 0
        : 1
    }
  }

  const goToPricing = () => {
    navigate('/pricing', { state: { updatePlans: true, selectedTab: getPricingTab() } })
    setModalToShow('')
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={closeModal} onCloseClick={() => {}}>
      <TrialPreEndModalStyled noTag={productToShow === 'noTag'}>
        {isLoadingPlans || isLoadingDomains ? (
          <ComponentLoader />
        ) : (
          <>
            <TrialPreEndModalTitle>
              {t(
                trialPeriod
                  ? 'trial_pre_end_modal.coming_to_an_end'
                  : 'trial_pre_end_modal.trial_is_ended'
              )}
            </TrialPreEndModalTitle>
            {productToShow === 'noTag' ? (
              <TrialPreEndModalNoTag>
                {t(
                  ableToExtendTrial
                    ? 'trial_pre_end_modal.extend_your_free_trial'
                    : 'trial_pre_end_modal.install_tracking_to_continue'
                )}
              </TrialPreEndModalNoTag>
            ) : (
              <>
                <TrialPreEndModalSubTitle>
                  {t('trial_pre_end_modal.subtitle')}
                </TrialPreEndModalSubTitle>
                <TrialPreEndModalYourPerformance>
                  {t('trial_pre_end_modal.your_performance_metrics')}
                </TrialPreEndModalYourPerformance>
              </>
            )}
            {productToShow === PRODUCT_TYPES.PAID_MARKETING && <PaidMarketingData />}
            {productToShow === PRODUCT_TYPES.BOT_ZAPPING && <BotMitigationData />}
            {productToShow === PRODUCT_TYPES.BUNDLE && <BundleData />}
            <ModalControlsStyled>
              <Button
                text={t(
                  ableToExtendTrial
                    ? 'trial_pre_end_modal.extend_trial'
                    : 'trial_pre_end_modal.stay_protected'
                )}
                onClick={handleClick}
              />
            </ModalControlsStyled>
          </>
        )}
      </TrialPreEndModalStyled>
    </Modal>
  )
}
